import {useCallback} from 'react'

import type {IAssignee} from '../../../../api/common-contracts'
import type {UpdateItemContext} from '../../../../api/memex/contracts'
import {useHorizontalGroupedBy} from '../../../../features/grouping/hooks/use-horizontal-grouped-by'
import {isItemMovingWithinSortedValue} from '../../../../features/sorting/is-item-moving-within-sorted-value'
import {DropSide} from '../../../../helpers/dnd-kit/drop-helpers'
import {type DropEvent, isDropOnGroup, isDropOnRow} from '../../../../hooks/drag-and-drop/types'
import {useGetUpdateForGroupDropEvent} from '../../../../hooks/drag-and-drop/use-group-drop-handler'
import {getFirstVisibleCell, rowIsExpanded} from '../../../../hooks/tanstack-table/helpers/row-api'
import type {Row} from '../../../../hooks/tanstack-table/helpers/types'
import {useTableInstance} from '../../../../hooks/tanstack-table/providers/table-provider'
import {useEnabledFeatures} from '../../../../hooks/use-enabled-features'
import {useSortedBy} from '../../../../hooks/use-sorted-by'
import {type UpdateItemActions, useUpdateAndReorderItem} from '../../../../hooks/use-update-item'
import type {MemexItemModel} from '../../../../models/memex-item-model'
import type {FocusAction} from '../../../../navigation/context'
import type {ReorderItemData} from '../../../../state-providers/memex-items/types'
import {Resources} from '../../../../strings'
import useToasts, {ToastType} from '../../../toasts/use-toasts'
import {
  getCopilotAssigneeAction,
  shouldOpenCopilotModal,
  useCopilotAssignmentModal,
} from '../../copilot-assignment-modal-provider'
import {type CellFocus, focusCell, type NavigateActionOptions, useStableTableNavigation} from '../../navigation'

/**
 * Checks if dropping activeRow onto overRow would create a cycle in the hierarchy.
 * Returns true if the overRow is a descendant of activeRow (i.e., activeRow is an ancestor of overRow).
 */
export function wouldCreateCycle(
  activeRow: Row<MemexItemModel> | undefined,
  overRow: Row<MemexItemModel> | undefined,
): boolean {
  if (!overRow || !activeRow) return false
  if (activeRow.subRows.length === 0) return false
  return overRow.getParentRows().some(r => r.id === activeRow.id)
}

/**
 * Strip fields that would result in no-ops. This avoids, for example, making an unecessary API call to update the grouped value
 * when dragging within the same group.
 */
const stripNoopFields = (event: DropEvent): DropEvent | undefined => {
  let strippedEvent: DropEvent | undefined = event

  // Remove start and end group if dragging in same group
  if (isDropOnGroup(strippedEvent)) {
    const {activeItemGroup: movingItemGroup, overItemGroup, ...rest} = strippedEvent

    // at this point if the event only contains the `movingItem` field, it's a total no-op and we can return undefined
    if (movingItemGroup.value === overItemGroup.value) strippedEvent = isDropOnRow(rest) ? rest : undefined
  }

  // Remove end row if dropped on source row
  if (strippedEvent && isDropOnRow(strippedEvent)) {
    const {overItem, side, ...rest} = strippedEvent

    if (strippedEvent.activeItem.id === overItem.id) strippedEvent = isDropOnGroup(rest) ? rest : undefined
  }

  return strippedEvent
}

/** Select the row at `index` and focus the first cell in it. */
export const selectAndFocusItem = ({
  itemId,
  navigationDispatch,
  flatRows,
}: {
  itemId: number
  navigationDispatch: (value: FocusAction<CellFocus, NavigateActionOptions>) => void
  flatRows: Array<Row<MemexItemModel>>
}) => {
  const newRow = flatRows.find(r => r.original.id === itemId)

  if (!newRow) return
  newRow.toggleSelected(true)

  const firstColId = getFirstVisibleCell(newRow)?.column.id
  if (firstColId) navigationDispatch(focusCell(newRow.id, firstColId, false, false))
}

export const useRowDropHandler = () => {
  const {updateAndReorderItem} = useUpdateAndReorderItem()
  const {addToast} = useToasts()
  const {sorts, clearSortedBy} = useSortedBy()
  const table = useTableInstance()
  const {navigationDispatch} = useStableTableNavigation()
  const {getUpdatesForGroupDropAndParentChangeEvent, handleGroupDropRequestError} = useGetUpdateForGroupDropEvent()
  const {toggleGroupCollapsed} = useHorizontalGroupedBy()
  const {tryOpenCopilotModal} = useCopilotAssignmentModal()
  const {memex_group_by_multi_value_changes} = useEnabledFeatures()

  /** If the group containing the row at `index` is collapsed, expand it. */
  const ensureExpandedGroup = useCallback(
    (overGroupId: string | undefined) => {
      const groupedRows = table.getPreExpandedRowModel().rows
      const group = groupedRows.find(g => g.groupQueryData.groupId === overGroupId)
      if (group && !rowIsExpanded(group)) {
        toggleGroupCollapsed(group.groupedValue)
      }
    },
    [table, toggleGroupCollapsed],
  )

  const performUpdate = useCallback(
    async (itemId: number, action: UpdateItemActions, context?: Pick<UpdateItemContext, 'parentIssueId'>) => {
      const updateColumnActions = action.updateColumnActions || []
      // If the update includes a parent issue update, we handle the error differently
      const hasParentIssueUpdate =
        updateColumnActions.filter(updateColumnAction => updateColumnAction.dataType === 'parentIssue').length > 0

      try {
        await updateAndReorderItem(itemId, action, {parentIssueId: context?.parentIssueId})

        ensureExpandedGroup(action.reorderData?.overGroupId)

        // `setTimeout` ensures the table has time to expand the group before operating on the row.
        // It's important that we get fresh `flatRows` inside the callback rather than capturing them
        // beforehand, because reparenting rebuilds the row model with new row IDs (which include
        // parent/group info in grouped views). Using stale row instances would cause
        // `row.toggleSelected` to reference an old ID that no longer exists in the current model.
        setTimeout(() => selectAndFocusItem({itemId, navigationDispatch, flatRows: table.getRowModel().flatRows}))
      } catch (error) {
        return handleGroupDropRequestError(error, hasParentIssueUpdate)
      }
    },
    [updateAndReorderItem, ensureExpandedGroup, navigationDispatch, table, handleGroupDropRequestError],
  )

  const groupedRows = table.getPreExpandedRowModel().rows

  /** Build the reorder for drops on rows. */
  const getReorderForEvent = useCallback(
    (event: DropEvent): ReorderItemData | undefined => {
      let overItemId = undefined
      let overItemSide = undefined
      let overGroupId = undefined

      const flatRows = table.getRowModel().flatRows
      // When finding rows, we need to skip group header rows because a group row's `original`
      // is set to the first item in the group, which could cause us to find the wrong row.
      const overRow =
        'overItem' in event ? flatRows.find(r => r.original.id === event.overItem.id && !r.getIsGrouped()) : undefined
      const activeRow =
        'activeItem' in event
          ? flatRows.find(r => r.original.id === event.activeItem.id && !r.getIsGrouped())
          : undefined

      // When moving an item in a flat table view list or within the same horizontal group:
      //   Require that the activeItem has the same sorted value(s) as the overItem before committing the reorder.
      //   Show a toast blocking the action if the sorted values are different.
      // When moving an item between groups in a grouped table view:
      //   Allow the action regardless of sorting, and include the reorder information in the update.
      //   This is a best effort and is consistent with the behavior of moving between columns in a sorted board view.
      //   If the 'sort by' column is the same as the 'group by' column, then it will correctly update the value and order.
      //   Else, the row might ultimately be moved to a place the user didn't drag after the sort is applied, which is OK.
      if (isDropOnRow(event)) {
        overItemId = event.overItem.id
        overItemSide = event.side

        // When dropping AFTER an expanded parent to nest as first child, use the first
        // sub-issue as the position reference with BEFORE instead. This ensures both
        // the optimistic update (query cache insertion) and the API call target the
        // correct sub-issue query, not the grandparent's.
        const isNestingAsFirstChild =
          event.side === DropSide.AFTER && 'overParentItem' in event && event.overParentItem?.id === event.overItem.id

        const firstSubIssue = overRow?.subRows?.[0]
        if (isNestingAsFirstChild && firstSubIssue) {
          overItemId = firstSubIssue.original.id
          overItemSide = DropSide.BEFORE
        }

        // Prevent dragging a parent row to a position within its own sub-rows
        if (wouldCreateCycle(activeRow, overRow)) {
          return undefined
        }

        // Get sorted items from flat rows, excluding group header rows
        const sortedItems = flatRows.filter(r => !r.getIsGrouped()).map(r => r.original)

        for (const sort of sorts) {
          if (!isItemMovingWithinSortedValue(event.activeItem, sort.column, sortedItems, overItemId, overItemSide)) {
            const action = {
              text: Resources.cannotReorderForSortAction,
              handleClick: () => clearSortedBy(),
            }

            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              message: Resources.cannotReorderForSortMessage,
              type: ToastType.warning,
              action,
            })

            return
          }
        }
      }

      if (isDropOnGroup(event)) {
        const group = groupedRows.find(r => r.groupedValue === event.overItemGroup.value)
        const groupId = group?.groupQueryData.groupId
        if (groupId) {
          overGroupId = groupId
        }
        // When dragging onto a group header or footer and no target row is available (ie dropped onto header or omnibar),
        // move to end of group
        if (!overItemId) {
          const lastItemInGroup = group?.subRows.at(-1)
          if (lastItemInGroup) {
            overItemId = lastItemInGroup.original.id
            overItemSide = DropSide.AFTER
          }
        }
      }

      // Prevent dragging a parent row to a position within its own sub-rows
      if (wouldCreateCycle(activeRow, overRow)) {
        return undefined
      }

      if (overItemId && overItemSide) {
        // Use the target parent if dropping onto a sub-issue position, otherwise undefined (top-level)
        const parentItemId = 'overParentItem' in event ? event.overParentItem?.id : undefined
        const sourceParentItemId = 'parentItem' in event ? event.parentItem?.id : undefined
        return {
          overItemId,
          side: overItemSide,
          overGroupId,
          parentItemId,
          sourceParentItemId,
        }
      }
      // If only overGroupId is set, we're dropping on an empty group
      return overGroupId ? {overGroupId} : undefined
    },
    [table, sorts, addToast, clearSortedBy, groupedRows],
  )

  return useCallback(
    async (event_: DropEvent) => {
      const event = stripNoopFields(event_)
      if (!event) return

      // Helper function to execute the drop logic
      const executeDropLogic = async () => {
        let updateColumnActions = await getUpdatesForGroupDropAndParentChangeEvent(event)
        const droppingOnGroup = isDropOnGroup(event)

        // Check if this is a Copilot assignment - if so, include the actor in the assignees
        if (droppingOnGroup && shouldOpenCopilotModal([event.activeItem], event.overItemGroup.sourceObject)) {
          updateColumnActions = [
            getCopilotAssigneeAction({
              targetAssignees: event.overItemGroup.sourceObject.value as Array<IAssignee>,
              existingAssignees: event.activeItem.columns.Assignees ?? [],
              keepExistingAssignees: memex_group_by_multi_value_changes ?? false,
            }),
          ]
        } else {
          // Don't continue with reordering if validation of the update failed - that could cause the item to end up
          // somewhere totally unexpected
          if (droppingOnGroup && updateColumnActions.length === 0) return
        }

        const reorderData = getReorderForEvent(event)
        // Extract target parent's issue ID (handles reparenting, same-parent reorder, and top-level moves)
        const targetParentIssueId = 'overParentItem' in event ? event.overParentItem?.content.id : undefined

        if (!reorderData && updateColumnActions.length === 0) return
        await performUpdate(
          event.activeItem.id,
          {
            reorderData,
            updateColumnActions,
          },
          {
            parentIssueId: targetParentIssueId,
          },
        )
      }

      // Check if we should open the Copilot assignment modal when dropping on a group
      if (isDropOnGroup(event)) {
        // Try to open the Copilot modal - if it opens, we're done (the modal will handle the move)
        const modalOpened = tryOpenCopilotModal([event.activeItem], event.overItemGroup.sourceObject, executeDropLogic)

        if (modalOpened) {
          return // Modal is handling the operation if assignment succeeds
        }
      }

      // Continue with normal drop logic if modal wasn't opened
      await executeDropLogic()
    },
    [
      getUpdatesForGroupDropAndParentChangeEvent,
      getReorderForEvent,
      memex_group_by_multi_value_changes,
      performUpdate,
      tryOpenCopilotModal,
    ],
  )
}
